import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Loader2, KeyRound, Copy, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Amministratore", desc: "Accesso completo alla gestione" },
  { value: "tecnico", label: "Tecnico", desc: "Compila i report di fine evento" },
  { value: "operativo", label: "Operativo", desc: "Solo timbrature entrata/uscita" },
];
const roleMeta = (v) => ROLES.find((r) => r.value === v) || ROLES[1];

export default function Accessi() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [tempPw, setTempPw] = useState({});
  const [lastLogin, setLastLogin] = useState({});

  const load = () => Promise.all([api.listStaff(), api.accessLastLogin()])
    .then(([s, ll]) => { setStaff(s); setLastLogin(ll || {}); setLoading(false); });
  const fmtLast = (iso) => { if (!iso) return "mai entrato"; const d = new Date(iso); return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  useEffect(() => { load(); }, []);

  const draftOf = (s) => drafts[s.id] || { email: s.login_email || "", role: s.login_role || "tecnico" };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...draftOf(staff.find((x) => x.id === id)), ...d[id], ...patch } }));

  const grant = async (s) => {
    const d = draftOf(s);
    if (!d.email.trim()) return toast.error("Inserisci l'email di accesso");
    try {
      const res = await api.grantAccess(s.id, { email: d.email.trim(), role: d.role });
      if (res.temp_password) { setTempPw((t) => ({ ...t, [s.id]: res.temp_password })); toast.success("Accesso creato! Password temporanea mostrata sotto."); }
      else toast.success("Accesso aggiornato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const reset = async (s) => {
    try { const res = await api.resetStaffPassword(s.id); setTempPw((t) => ({ ...t, [s.id]: res.temp_password })); toast.success("Nuova password temporanea generata"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const revoke = async (s) => {
    try { await api.revokeAccess(s.id); setTempPw((t) => ({ ...t, [s.id]: null })); toast.success("Accesso revocato"); load(); }
    catch { toast.error("Errore"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>;

  const withAccess = staff.filter((s) => s.can_login);
  const withoutAccess = staff.filter((s) => !s.can_login);

  const Row = ({ s }) => {
    const d = draftOf(s);
    const pw = tempPw[s.id];
    return (
      <div data-testid={`accesso-row-${s.id}`} className="bg-[#121824] border border-[#222D42] rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><p className="text-slate-100 font-medium">{s.name}</p>{s.can_login ? <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${s.login_role === "admin" ? "bg-amber-500/10 text-amber-400" : s.login_role === "tecnico" ? "bg-emerald-500/10 text-emerald-400" : "bg-sky-500/10 text-sky-300"}`}>{roleMeta(s.login_role).label}</span> : <span className="text-[11px] text-slate-500">Nessun accesso</span>}</div>
          {s.can_login && <div className="text-right"><p className="text-xs text-slate-500">{s.login_email}</p><p data-testid={`accesso-lastlogin-${s.id}`} className="text-[10px] text-slate-600">Ultimo accesso: {fmtLast(lastLogin[s.login_email])}</p></div>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="lbl">Email di accesso</label><input data-testid={`accesso-email-${s.id}`} value={d.email} onChange={(e) => setDraft(s.id, { email: e.target.value })} className="inp" placeholder="nome@betti.it" /></div>
          <div><label className="lbl">Livello di autorizzazione</label><select data-testid={`accesso-role-${s.id}`} value={d.role} onChange={(e) => setDraft(s.id, { role: e.target.value })} className="inp">{ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
        </div>
        <p className="text-[11px] text-slate-600">{roleMeta(d.role).desc}</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid={`accesso-grant-${s.id}`} onClick={() => grant(s)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-3 py-1.5 rounded-lg text-sm transition-colors">{s.can_login ? "Aggiorna accesso" : "Crea accesso"}</button>
          {s.can_login && <button data-testid={`accesso-reset-${s.id}`} onClick={() => reset(s)} className="bg-[#1A2335] hover:bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Reimposta password</button>}
          {s.can_login && <button data-testid={`accesso-revoke-${s.id}`} onClick={() => revoke(s)} className="bg-[#1A2335] hover:bg-red-500/10 text-red-400 border border-[#222D42] px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Revoca</button>}
        </div>
        {pw && (
          <div data-testid={`accesso-temp-pw-${s.id}`} className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 space-y-1">
            <p className="text-[11px] text-amber-400/80 uppercase tracking-wider">Password temporanea — copiala e inviala</p>
            <div className="flex items-center gap-2">
              <code className="font-mono text-base text-amber-300 bg-[#0d1320] rounded px-2 py-1 flex-1 select-all">{pw}</code>
              <button onClick={() => { navigator.clipboard?.writeText(pw); toast.success("Password copiata"); }} className="p-2 rounded-md bg-[#1A2335] hover:bg-[#222D42] text-slate-300"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-500">Accesso con <span className="text-slate-300">{d.email}</span>. La persona potrà poi cambiarla da "Password dimenticata".</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-slate-100">Accessi Personale</h1>
        <p className="text-slate-500 text-sm mt-1">Gestisci email, password e livello di autorizzazione del personale autorizzato</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {ROLES.map((r) => (
          <div key={r.value} className="bg-[#121824] border border-[#222D42] rounded-xl p-4 flex items-start gap-3">
            <ShieldCheck className={`w-5 h-5 mt-0.5 ${r.value === "admin" ? "text-amber-400" : r.value === "tecnico" ? "text-emerald-400" : "text-sky-300"}`} />
            <div><p className="text-slate-100 text-sm font-medium">{r.label}</p><p className="text-[11px] text-slate-500">{r.desc}</p></div>
          </div>
        ))}
      </div>

      {withAccess.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-head text-base font-semibold text-slate-200 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Con accesso attivo ({withAccess.length})</h3>
          {withAccess.map((s) => <Row key={s.id} s={s} />)}
        </div>
      )}

      <div className="space-y-3">
        <h3 className="font-head text-base font-semibold text-slate-200">Senza accesso ({withoutAccess.length})</h3>
        {withoutAccess.length === 0 ? <p className="text-slate-600 text-sm">Tutto il personale ha un accesso.</p> : withoutAccess.map((s) => <Row key={s.id} s={s} />)}
      </div>

      <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}.lbl{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;color:#64748B;font-weight:500;display:block;margin-bottom:.375rem}`}</style>
    </div>
  );
}
